import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';

import AuthorizationContainer from '../../../components/AuthorizationContainer';
import EstateUnitFilter from '../../../components/EstateUnitFilter';
import FilterBar, {
  FilterField,
  FilterValues,
  toDateOnlyString,
  toFilterIdList
} from '../../../components/Filter';
import PopupModal from '../../../components/PopupModal';
import { CustoDropDownItemProps } from '../../../components/types';
import { GetBondInsurancePoliciesListResponseDTO } from '../../../dtos/bond-insurance-policy-list/get-bond-insurance-policies-list-response-dto';
import { PolicyForGetBondInsurancePoliciesListResponseDTO } from '../../../dtos/bond-insurance-policy-list/interfaces/policy-for-get-bond-insurance-policies-list-response-dto';
import { ActionEnum } from '../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../enums/authz-feature.enum';
import { BondInsuranceHiredDataStatusEnum } from '../../../enums/bond-insurance-hired-data-status.enum';
import {
  getBondInsurancePoliciesList,
  sortBondInsurancePolicies
} from '../../../services/bond-insurance-policy';
import { useDropDownUsers } from '../../../services/broker';
import { dangerNotification } from '../../../services/notification';
import { iRootState } from '../../../store';
import { AvailableBrokersAndEstatesState } from '../../../store/types/temp-types';
import { BannerLocationActivationEnum } from '../../../types/Banner';
import { InsurerState } from '../../../types/InsurerState';
import { UsersToCustomDropDownOptions } from '../../../util';
import { appendEstateUnitIdToFilter } from '../../../utils/estateUnitFilter.util';
import { isBackOfficeUser } from '../../../utils/isBackOfficeUser';
import { isEstateRole } from '../../../utils/isEstateRole';
import { typeOptions } from '../../find-record/types/options-find.type';
import { PoliciesFilters, PoliciesListingRouteInfo } from '../types';
import BondInsurancePoliciesTable from './BondInsurancePoliciesTable';
import CreatePolicyModal from './CreatePolicyModal';

export type BondInsurancePoliciesListProps = {
  availableBrokersAndEstates: iRootState['availableBrokersAndEstates'];
  user: iRootState['user'];
  updateAvailableBrokersAndEstates: (
    availableBrokers: AvailableBrokersAndEstatesState
  ) => AvailableBrokersAndEstatesState | void;
} & RouteComponentProps<PoliciesListingRouteInfo>;

interface LocationState {
  bondInsuranceId?: string;
  expireInDays?: string;
}

enum MoreFiltersEnum {
  unread = 'onlyUnread',
  noPolicy = 'noPolicyAttached',
  warning = 'hasWarning',
  quiver = 'quiver',
  manual = 'manual',
  issuedNew = 'issuedNew'
}

type SearchField =
  | 'document'
  | 'property_document'
  | 'name'
  | 'property_name'
  | 'address'
  | 'insurerPolicyId'
  | 'policyId';

type ApiFilters = PoliciesFilters & { estateUnitId?: string };

const SEARCH_FIELDS: SearchField[] = [
  'document',
  'property_document',
  'name',
  'property_name',
  'address',
  'insurerPolicyId',
  'policyId'
];

const DOCUMENT_SEARCH_FIELDS: SearchField[] = ['document', 'property_document'];

const statusOptions = [
  {
    value: BondInsuranceHiredDataStatusEnum.signaturePending,
    text: 'Assinatura Pendente'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.insurerPolicyPending,
    text: 'Emissão de Apólice Pendente pela Seguradora'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.underRenewal,
    text: 'Em renovação'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.renewedCreated,
    text: 'Renovada'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.notRenewed,
    text: 'Não Renovada'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.insurerPolicyCreated,
    text: 'Emitida'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.renewCanceled,
    text: 'Renovação Cancelada'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.overdue,
    text: 'Vencida'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.overdueIssued,
    text: 'Vencida Emitida'
  },
  {
    value: BondInsuranceHiredDataStatusEnum.expired,
    text: 'Expirada'
  }
];

const expireInDaysOptions = [
  { value: '7', text: '7 dias' },
  { value: '30', text: '30 dias' }
];

const moreFiltersSelectOptions = [
  { value: MoreFiltersEnum.unread, text: 'Follow-up não lido' },
  { value: MoreFiltersEnum.noPolicy, text: 'Sem apólice anexada' },
  { value: MoreFiltersEnum.warning, text: 'Apólice Inconsistente' },
  { value: MoreFiltersEnum.quiver, text: 'Importado do Quiver' },
  { value: MoreFiltersEnum.manual, text: 'Inserido Manualmente' },
  { value: MoreFiltersEnum.issuedNew, text: 'Emitida Nova' }
];

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  const dateOnly = toDateOnlyString(value);
  return dateOnly || undefined;
};

const buildInitialFilterValues = (
  singleEstateId: string,
  locationState?: LocationState
): FilterValues => ({
  type: 'document',
  argument: '',
  insurer: [],
  estateId: singleEstateId,
  start: '',
  end: '',
  status: [],
  expireInDays: locationState?.expireInDays || '',
  estateUnitId: '',
  renewResponsible: '',
  accountManager: '',
  moreFilters: ''
});

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = values.type as SearchField | undefined;
  let argument = String(values.argument || '').trim();

  if (searchType && DOCUMENT_SEARCH_FIELDS.includes(searchType)) {
    argument = argument.replace(/\D/g, '');
  }

  const filter: ApiFilters = {};

  SEARCH_FIELDS.forEach(field => {
    filter[field] = '';
  });

  if (searchType && argument && SEARCH_FIELDS.includes(searchType)) {
    filter[searchType] = argument;
  }

  const insurerIds = toFilterIdList(values.insurer);
  if (insurerIds) filter.insurer = insurerIds;
  if (values.estateId) filter.estateId = String(values.estateId);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  const statuses = toFilterIdList(values.status)?.filter(
    status => status !== 'ALL' && status !== 'Todos'
  );
  if (statuses?.length) {
    filter.status = statuses;
  }

  const expireInDays = String(values.expireInDays || '');
  if (expireInDays) {
    filter.expireInDays = expireInDays;
    filter.status = BondInsuranceHiredDataStatusEnum.insurerPolicyCreated;
  }

  if (values.accountManager) {
    filter.accountManager = String(values.accountManager);
  }

  if (values.renewResponsible) {
    filter.renewResponsible = String(values.renewResponsible);
  }

  const moreFilters = String(values.moreFilters || '');
  filter.onlyUnread = moreFilters === MoreFiltersEnum.unread;
  filter.noPolicyAttached = moreFilters === MoreFiltersEnum.noPolicy;
  filter.hasWarning = moreFilters === MoreFiltersEnum.warning;
  filter.onlyQuiver = moreFilters === MoreFiltersEnum.quiver;
  filter.onlyManual = moreFilters === MoreFiltersEnum.manual;
  filter.onlyNew = moreFilters === MoreFiltersEnum.issuedNew;

  return appendEstateUnitIdToFilter(
    filter as Record<string, unknown>,
    String(values.estateUnitId || '')
  ) as ApiFilters;
};

const BondInsurancePoliciesPage = ({
  availableBrokersAndEstates,
  user
}: BondInsurancePoliciesListProps) => {
  const location = useLocation();
  const history = useHistory();
  const locationState = location.state as LocationState | undefined;

  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const insurers: InsurerState[] = availableBrokersAndEstates.bondInsurers.map(
    (insurer: any) => insurer.value
  );

  const isBackOffice = isBackOfficeUser();
  const isEstateUser = isEstateRole(user?.role);

  const singleEstateId = useMemo(() => {
    if (estates.length !== 1) return '';
    const value = estates[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estates]);

  const initialFilterValues = useMemo(
    () => buildInitialFilterValues(singleEstateId, locationState),
    // Seed expireInDays from first navigation state; singleEstateId may arrive with redux.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleEstateId]
  );

  const hasDeepLink = Boolean(
    locationState?.bondInsuranceId || locationState?.expireInDays
  );

  const [bondInsurancePolicies, setBondInsurancePolicies] = useState<
    PolicyForGetBondInsurancePoliciesListResponseDTO[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ascendingOrder] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataCount, setDataCount] = useState(0);
  const [bondInsuranceId, setBondInsuranceId] = useState<string | undefined>(
    locationState?.bondInsuranceId
  );
  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(
    hasDeepLink || !isBackOffice
  );

  const appliedFiltersRef = useRef(appliedFilters);
  const bondInsuranceIdRef = useRef(bondInsuranceId);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    bondInsuranceIdRef.current = bondInsuranceId;
  }, [bondInsuranceId]);

  const selectableInsurers = useMemo(
    () =>
      insurers.map(insurer => ({
        value: insurer.id,
        text: insurer.name
      })),
    [insurers]
  );

  const insurersList = useMemo(
    () =>
      insurers.map(insurer => ({
        key: insurer.id,
        value: insurer.id,
        text: insurer.name
      })),
    [insurers]
  );

  const estateOptions = useMemo(
    () =>
      estates.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [estates]
  );

  const {
    data: usersData,
    isLoading: isAccountManagersLoading,
    isError
  } = useDropDownUsers(user?.broker?.id);

  useEffect(() => {
    if (isError) {
      dangerNotification(
        'Oops...',
        'Não foi possível encontrar os analistas disponíveis'
      );
    }
  }, [isError]);

  const accountManagerOptions = useMemo(() => {
    if (!usersData) return [];

    return UsersToCustomDropDownOptions(
      false,
      usersData.map((item: any) => ({
        id: item.id,
        name: item.name
      }))
    ).map((item: any) => ({
      value: String(item.value),
      text: String(item.text)
    }));
  }, [usersData]);

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'multiSelect',
        name: 'insurer',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(selectableInsurers)
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'estateId',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        getDisplayValue: values => {
          const option = estateOptions.find(
            item => String(item.value) === String(values.estateId)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'multiSelect',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusOptions)
      },
      {
        kind: 'select',
        name: 'expireInDays',
        label: 'Expira em',
        placeholder: 'Mostrar todas',
        options: toSelectOptions(expireInDaysOptions),
        getDisplayValue: values => {
          const option = expireInDaysOptions.find(
            item => String(item.value) === String(values.expireInDays)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(values.estateId || user?.estate?.id || '')}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    if (!isEstateUser) {
      fields.push(
        {
          kind: 'select',
          name: 'renewResponsible',
          label: 'Responsável pela Renovação',
          placeholder: 'Selecionar...',
          advanced: true,
          loading: isAccountManagersLoading,
          options: toSelectOptions(accountManagerOptions),
          getDisplayValue: values => {
            const option = accountManagerOptions.find(
              item => String(item.value) === String(values.renewResponsible)
            );
            return option ? option.text : undefined;
          }
        },
        {
          kind: 'select',
          name: 'accountManager',
          label: 'Gestor de Contas',
          placeholder: 'Selecionar...',
          advanced: true,
          loading: isAccountManagersLoading,
          options: toSelectOptions(accountManagerOptions),
          getDisplayValue: values => {
            const option = accountManagerOptions.find(
              item => String(item.value) === String(values.accountManager)
            );
            return option ? option.text : undefined;
          }
        },
        {
          kind: 'select',
          name: 'moreFilters',
          label: 'Mais Filtros',
          placeholder: 'Selecionar...',
          advanced: true,
          options: toSelectOptions(moreFiltersSelectOptions),
          getDisplayValue: values => {
            const option = moreFiltersSelectOptions.find(
              item => String(item.value) === String(values.moreFilters)
            );
            return option ? option.text : undefined;
          }
        }
      );
    }

    return fields;
  }, [
    isEstateUser,
    selectableInsurers,
    estateOptions,
    accountManagerOptions,
    isAccountManagersLoading,
    user?.estate?.id
  ]);

  const loadTotal = useCallback(
    async (filter: ApiFilters) => {
      const data: GetBondInsurancePoliciesListResponseDTO =
        await getBondInsurancePoliciesList(currentPage, {
          ...filter,
          action: 'COUNT',
          isRv: true
        });
      setDataCount(data.count);
    },
    [currentPage]
  );

  const getData = useCallback(async () => {
    setIsLoading(true);

    try {
      const filter: ApiFilters = {
        ...appliedFiltersRef.current,
        isRv: true,
        ...(bondInsuranceIdRef.current
          ? { bondInsuranceId: bondInsuranceIdRef.current }
          : {})
      };

      if (!filter.estateId && singleEstateId) {
        filter.estateId = singleEstateId;
      }

      const data: GetBondInsurancePoliciesListResponseDTO =
        await getBondInsurancePoliciesList(currentPage, filter);

      const orderedPolicies: PolicyForGetBondInsurancePoliciesListResponseDTO[] =
        data.policies.length > 1
          ? sortBondInsurancePolicies(data.policies, ascendingOrder)
          : data.policies;

      if (currentPage === 1) {
        await loadTotal(filter);
      }

      setBondInsurancePolicies(orderedPolicies);

      if (bondInsuranceIdRef.current) {
        setBondInsuranceId(undefined);
      }
    } catch (e) {
      dangerNotification('Oops...', 'Não foi possível buscar as apólices');
    } finally {
      setIsLoading(false);
    }
  }, [ascendingOrder, currentPage, loadTotal, singleEstateId]);

  const updateList = useCallback(async () => {
    await getData();
  }, [getData]);

  useEffect(() => {
    const state = location.state as LocationState | undefined;
    if (!state?.bondInsuranceId) return;

    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: {
        ...state,
        bondInsuranceId: undefined
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isBackOffice) {
      setIsEnabledToFetch(true);
    }
  }, [isBackOffice]);

  useEffect(() => {
    if (!isEnabledToFetch) return;
    getData();
  }, [currentPage, appliedFilters, isEnabledToFetch, getData]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    if (!next.estateId && singleEstateId) {
      next.estateId = singleEstateId;
    }
    setBondInsuranceId(undefined);
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const handleFilterClear = () => {
    setBondInsuranceId(undefined);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <AuthorizationContainer
          action={ActionEnum.create}
          feature={FeatureEnum.policies}
        >
          <CreatePolicyModal
            insurers={insurersList}
            estatesOptions={availableBrokersAndEstates.estates || []}
          />
        </AuthorizationContainer>
      </div>

      <FilterBar
        fields={filterFields}
        initialValues={initialFilterValues}
        onSearch={handleFilterSearch}
        onClear={handleFilterClear}
        loading={isLoading}
        syncToUrl
        urlNamespace="policies"
        hasExternalActiveFilters={Boolean(bondInsuranceId)}
      />

      <BondInsurancePoliciesTable
        updateList={updateList}
        bondInsurancePolicies={bondInsurancePolicies}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        getData={getData}
        dataCount={dataCount}
      />

      <PopupModal
        locationActivation={BannerLocationActivationEnum.BOND_INSURANCE_POLICY_LIST}
      />
    </>
  );
};

export default BondInsurancePoliciesPage;
